import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, GripVertical, Plus, MoreHorizontal, Phone, Mail, Building2,
  DollarSign, Zap, MapPin, Calendar, ChevronRight, X, Loader2, AlertTriangle, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: 'New Lead', color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'Contacted', color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'Qualified', color: '#F59E0B', bg: '#FFFBEB' },
  { id: 'Proposal Sent', color: '#06B6D4', bg: '#ECFEFF' },
  { id: 'Negotiating', color: '#EC4899', bg: '#FDF2F8' },
  { id: 'Closed Won', color: '#10B981', bg: '#ECFDF5' },
  { id: 'Closed Lost', color: '#6B7280', bg: '#F3F4F6' },
];

const STALE_DAYS = 7;

const isStale = (deal) => {
  if (deal.stage === 'Closed Won' || deal.stage === 'Closed Lost') return false;
  const lastUpdate = deal.updated_at || deal.created_at || deal.timestamp;
  if (!lastUpdate) return true;
  const lastDate = new Date(lastUpdate);
  const now = new Date();
  const diffDays = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
  return diffDays >= STALE_DAYS;
};

const getDaysSinceUpdate = (deal) => {
  const lastUpdate = deal.updated_at || deal.created_at || deal.timestamp;
  if (!lastUpdate) return 999;
  const lastDate = new Date(lastUpdate);
  const now = new Date();
  return Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
};

const PipelineKanban = ({ onDealClick, onRefresh }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const fetchDeals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/deals`, { withCredentials: true });
      setDeals(data.deals || []);
    } catch (err) {
      console.error('Failed to fetch deals:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', deal.id);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedDeal || draggedDeal.stage === newStage) {
      setDraggedDeal(null);
      return;
    }

    // Optimistic update
    setDeals(prev => prev.map(d => 
      d.id === draggedDeal.id ? { ...d, stage: newStage } : d
    ));

    try {
      await axios.put(
        `${API}/api/admin/deals/${draggedDeal.id}/stage`,
        { stage: newStage },
        { withCredentials: true }
      );
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update stage:', err);
      fetchDeals(); // Revert on error
    }
    
    setDraggedDeal(null);
  };

  const getDealsByStage = (stageId) => {
    return deals.filter(d => (d.stage || 'New Lead') === stageId);
  };

  const formatValue = (value) => {
    if (!value) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    return `$${num.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 400 }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div 
      data-testid="pipeline-kanban"
      style={{
        display: 'flex',
        gap: '0.75rem',
        overflowX: 'auto',
        padding: '0.5rem 0',
        minHeight: 500,
      }}
    >
      {STAGES.map(stage => {
        const stageDeals = getDealsByStage(stage.id);
        const isDropTarget = dragOverStage === stage.id;
        
        return (
          <div
            key={stage.id}
            data-testid={`kanban-column-${stage.id.replace(/\s+/g, '-').toLowerCase()}`}
            onDragOver={(e) => handleDragOver(e, stage.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage.id)}
            style={{
              minWidth: 280,
              maxWidth: 280,
              flex: '0 0 280px',
              display: 'flex',
              flexDirection: 'column',
              background: isDropTarget ? stage.bg : '#FAFBFC',
              borderRadius: 8,
              border: isDropTarget ? `2px dashed ${stage.color}` : '1px solid #E5E7EB',
              transition: 'all 0.2s ease',
            }}
          >
            {/* Column Header */}
            <div style={{
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: stage.color,
                }} />
                <span style={{
                  fontSize: '0.813rem',
                  fontWeight: 600,
                  color: 'var(--navy)',
                }}>
                  {stage.id}
                </span>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: 100,
                  background: stage.bg,
                  color: stage.color,
                }}>
                  {stageDeals.length}
                </span>
              </div>
            </div>

            {/* Cards Container */}
            <div style={{
              flex: 1,
              padding: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              overflowY: 'auto',
            }}>
              {stageDeals.length === 0 ? (
                <div style={{
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '0.75rem',
                }}>
                  No deals
                </div>
              ) : stageDeals.map(deal => {
                const dealIsStale = isStale(deal);
                const daysSince = getDaysSinceUpdate(deal);
                
                return (
                <div
                  key={deal.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, deal)}
                  onClick={() => onDealClick && onDealClick(deal)}
                  data-testid={`kanban-card-${deal.id}`}
                  style={{
                    background: dealIsStale ? '#FEF2F2' : '#fff',
                    borderRadius: 6,
                    border: dealIsStale ? '1px solid #FECACA' : '1px solid #E5E7EB',
                    padding: '0.75rem',
                    cursor: 'grab',
                    transition: 'all 0.15s ease',
                    opacity: draggedDeal?.id === deal.id ? 0.5 : 1,
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = dealIsStale ? '#F87171' : stage.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = dealIsStale ? '#FECACA' : '#E5E7EB';
                  }}
                >
                  {/* Stale Indicator */}
                  {dealIsStale && (
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      display: 'flex', alignItems: 'center', gap: '0.25rem',
                      fontSize: '0.6rem', fontWeight: 600, color: '#DC2626',
                      background: '#FEE2E2', padding: '2px 5px', borderRadius: 4,
                    }}>
                      <AlertTriangle size={10} />
                      {daysSince}d stale
                    </div>
                  )}
                  
                  {/* Card Header */}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--navy)',
                      marginBottom: '0.125rem',
                      paddingRight: dealIsStale ? '60px' : 0,
                    }}>
                      {deal.name || 'Unknown'}
                    </div>
                    {deal.company && (
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#6B7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}>
                        <Building2 size={10} />
                        {deal.company}
                      </div>
                    )}
                  </div>

                  {/* Card Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {deal.email && (
                      <div style={{ fontSize: '0.7rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Mail size={10} /> {deal.email}
                      </div>
                    )}
                    {deal.state && (
                      <div style={{ fontSize: '0.7rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <MapPin size={10} /> {deal.state}
                      </div>
                    )}
                    {deal.energy_type && (
                      <div style={{ fontSize: '0.7rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Zap size={10} /> {deal.energy_type}
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div style={{
                    marginTop: '0.625rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #F3F4F6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    {(deal.deal_value || deal.annual_savings) && (
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#059669',
                      }}>
                        {formatValue(deal.deal_value || deal.annual_savings)}
                      </span>
                    )}
                    {deal.assigned_to_name && (
                      <div style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: 100,
                        background: '#F3F4F6',
                        color: '#6B7280',
                      }}>
                        {deal.assigned_to_name}
                      </div>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PipelineKanban;
